import { EventNames } from '../../../common/constants/constants';
import { getAttempt, isLeaving, msSinceAttempt, noteUploadProgress, setAttemptMeta } from '../../analytics/telemetry';
import { IEventTracker } from '../../../common/hooks/useTrackEvent';
import { CHUNK_ALIGNMENT, getChunkSizeFromUploadSpeed } from '../../../pages/VideoToolPage/utils';
import { patch as patchPendingUpload } from '../../upload/pendingUploadStore';
import { markPartConfirmed } from '../../upload/uploadQuiet';

/**
 * Thrown when GCS rejects a chunk PUT with 410 Gone — the resumable session
 * URI has expired (TTL is 7 days from session creation). Caller must mint a
 * new URL via `createResumableUploadUrl` and start a fresh `Upload`.
 *
 * The internal chunk-retry loop deliberately does NOT retry this error —
 * burning retries on a dead URL is pure waste.
 */
export class SessionExpiredError extends Error {
  constructor(message: string = 'GCS resumable session URI expired (410 Gone)') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

/**
 * Thrown when the upload was deliberately halted via `pause()` before bytes
 * were fully committed. Callers must treat this as a benign exit — do NOT
 * clear `isUploadInprogress`, do NOT post the story, do NOT surface an error
 * banner. The persisted `pendingUpload` record (status: `paused_wifi`) is
 * the durable handle for resuming later.
 */
export class UploadPausedError extends Error {
  constructor(message: string = 'Upload paused by user') {
    super(message);
    this.name = 'UploadPausedError';
  }
}

/**
 * Thrown when the per-chunk stall watchdog fires: the PUT made no upload
 * progress for `stallTimeoutMs`, so we aborted it.
 *
 * Deliberately NOT an `AbortError`. `AbortError` is reserved for a *user*
 * pause (`pause()` → `UploadPausedError`), which halts the upload silently;
 * a stall must stay retryable or the upload would stop dead instead of
 * recovering. The retry path keys off this type to shrink the part and resume
 * mid-chunk — see `uploadChunk`.
 */
export class ChunkStalledError extends Error {
  constructor(message: string = 'Chunk upload stalled — no upload progress within timeout') {
    super(message);
    this.name = 'ChunkStalledError';
  }
}

/**
 * Thrown when `wake()` aborts a part that went silent while the page was
 * hidden or offline. Retryable like a stall, but it says nothing about the
 * link's speed, so the retry path neither shrinks the part nor spends the
 * retry budget on it — see `uploadChunk`.
 */
export class WakeAbortError extends Error {
  constructor(message: string = 'Part aborted on return: no progress since the page went hidden or offline') {
    super(message);
    this.name = 'WakeAbortError';
  }
}

/** Why the app is nudging the running upload. */
export type WakeReason = 'visible' | 'online' | 'retry_timer' | 'user';
/**
 * What `wake()` did:
 *  - woke_backoff: cancelled a retry backoff sleep; the retry runs now.
 *  - woke_retry: a retry was being prepared (resume probe in flight); its
 *    backoff is skipped.
 *  - aborted_stalled_part: the in-flight part had made no progress since the
 *    page went hidden/offline, nor during the grace window; aborted and
 *    resumed from the server-confirmed byte.
 *  - noop_progressing: bytes are moving (or the part is fresh); left alone.
 *  - noop_awaiting_ack: every byte of the part is in the socket and only the
 *    server's answer is pending; the stall timer owns that wait.
 *  - noop_probe: the opening offset probe is in flight; left to finish.
 *  - noop_offline: a retry is waiting but the browser is still offline; a
 *    retry now would fail at once, so the backoff stands.
 *  - noop_idle: the engine is not running (errored, paused, or done); the
 *    app's own restart path handles that.
 */
export type WakeOutcome =
  | 'woke_backoff'
  | 'woke_retry'
  | 'aborted_stalled_part'
  | 'noop_progressing'
  | 'noop_awaiting_ack'
  | 'noop_probe'
  | 'noop_offline'
  | 'noop_idle';

export interface ChunkMeasurement {
  /** Size of the chunk just sent, in bytes. */
  chunkBytes: number;
  /** Smoothed throughput in Mbps after this chunk. */
  mbpsEwma: number;
  /** Bytes committed server-side after this chunk. */
  uploadedBytes: number;
  /** Total file size, repeated for convenience. */
  totalBytes: number;
  /** Estimated remaining seconds. null when smoothed speed isn't yet known. */
  etaSeconds: number | null;
}

export interface UploadOptions {
  id: string; // Unique identifier for each upload session
  uploadUrl: string;
  file: File;
  initialChunkSize?: number;
  maxRetries?: number;
  retryDelay?: number;
  /**
   * Max time (ms) a single chunk PUT may make no upload progress before it's
   * aborted as a retryable failure. Guards against dead-socket hangs where the
   * request fires neither `load` nor `error`. Default 15000.
   */
  stallTimeoutMs?: number;
  trackEvent: IEventTracker;
  onProgress?: (progress: {
    totalBytes: number;
    uploadedBytes: number;
    chunkIndex: number;
    chunkLength: number;
  }) => void;
  /**
   * Fires after each successful chunk PUT, with smoothed throughput and ETA.
   * Caller dispatches Redux state (etaSeconds, isSlowConnection) from this.
   */
  onChunkMeasured?: (measurement: ChunkMeasurement) => void;
}

export class Upload {
  /** Smoothed (EWMA) per-chunk Mbps. Public for UI / telemetry surfaces. */
  public speed: number;
  public readonly id: string;
  private readonly uploadUrl: string;
  private uploadedBytes: number = 0;
  public chunkSize: number;
  private isPaused: boolean = false;
  /**
   * Aborts the in-flight chunk PUT when `pause()` is called mid-fetch. Without
   * this, the current chunk would complete naturally before the loop noticed
   * the pause flag — wasting bandwidth and (worse) letting `uploadedBytes`
   * climb past the user's intended stop point.
   */
  private chunkAbortController: AbortController | null = null;
  /**
   * Highest `uploadedBytes` value we've reported via `onProgress` so far.
   * Used to prevent the progress ring from visibly rewinding when a chunk
   * fails mid-flight and retries — without this, intermediate XHR upload-
   * progress events on the retry attempt would start at `offset` again,
   * appearing to go backward from the prior attempt's high-water mark.
   */
  private maxReportedBytes: number = 0;
  private readonly file: File;
  private readonly maxRetries: number;
  private readonly retryDelay: number;
  private readonly stallTimeoutMs: number;
  private readonly onProgress?: (progress: {
    totalBytes: number;
    uploadedBytes: number;
    chunkIndex: number;
    chunkLength: number;
  }) => void;
  private readonly onChunkMeasured?: (measurement: ChunkMeasurement) => void;
  private readonly trackEvent: IEventTracker;
  private isUploading: boolean = false;

  /**
   * Exponential-weighted moving average of per-chunk Mbps. Null until the
   * first chunk completes, then seeded with that first measurement, then
   * smoothed on each subsequent chunk to prevent chunk-size oscillation on
   * variance spikes. α=0.3 — new sample contributes 30%, prior EWMA 70%.
   */
  private mbpsEwma: number | null = null;
  /**
   * Smoothed per-part server-ack wait (seconds) — the measured cost the
   * ratio-based part sizing amortizes. Null until the first part completes
   * with a measurable ack; sizing falls back to the conservative fixed tiers
   * until then, which also keeps the OPENER conservative when the cached
   * landing speed is stale (a review finding on the earlier fixed-seconds
   * approach: a 40 Mbps Wi-Fi cache must not bet a 28 MiB first part on
   * whatever network the user is on now).
   */
  private ackEwmaSec: number | null = null;
  private static readonly EWMA_ALPHA = 0.3;
  /** Ceiling on a single throughput sample fed to the EWMA (sanity clamp). */
  private static readonly MAX_SPEED_SAMPLE_MBPS = 300;
  /**
   * Floor for the EWMA after a stall downgrade. Keeps a zero-byte stall from
   * pinning throughput (and therefore part sizing and ETA) at rock bottom for
   * the remainder of the upload — the next successful part blends back up
   * from here at the normal α.
   */
  private static readonly MIN_EWMA_MBPS = 0.25;
  /**
   * Timeout for the 308 offset probe (`getUploadedBytes`). The probe is a
   * header-only PUT with an empty body — one round-trip, no payload — so a
   * healthy answer arrives in well under a second even on bad cellular.
   * `stallTimeoutMs` (15s) is sized for judging multi-megabyte transfers and
   * is mismatched patience here: if the answer hasn't arrived in 5s it isn't
   * coming, and every extra second is frozen-ring time during recovery. On
   * timeout the caller falls back to retrying from the chunk boundary — the
   * pre-probe behavior — so the cap can only cost the optimization, never the
   * upload.
   */
  private static readonly PROBE_TIMEOUT_MS = 5000;
  /**
   * Emergency part-size floor for the stall path. The normal floor
   * (MINCHUNKSIZE, 5 MiB) is an efficiency choice — fewer requests, less
   * overhead — but on a link that repeatedly dies mid-part, a 5 MiB part is
   * ~100 s of exposure at 0.4 Mbps. GCS accepts non-final parts down to
   * 256 KiB, so after a stall we allow halving below the normal floor, to
   * 1 MiB: small enough to confirm quickly on a collapsing line, large
   * enough to keep request overhead sane. Only the stall path uses this —
   * `adjustChunkSize` (driven by successful parts) still sizes within
   * [MINCHUNKSIZE, MAXCHUNKSIZE], so one successful part returns the upload
   * to the normal regime, and a further stall re-shrinks within one cycle.
   */
  private static readonly STALL_MIN_CHUNK_BYTES = 1024 * 1024;
  /**
   * How long `wake()` watches a silent in-flight part for a sign of life
   * before aborting it. A live socket that was merely throttled while hidden
   * reports progress within a few ticks (~50–100 ms each) of the page
   * returning; a socket the OS killed reports nothing.
   */
  private static readonly WAKE_GRACE_MS = 1000;

  /* ─── wake() state ─── */
  /** Resolves the current retry backoff sleep early; null when not sleeping. */
  private backoffWake: (() => void) | null = null;
  /** True from a retryable failure until its retry PUT starts. */
  private retrying: boolean = false;
  /** A wake arrived mid-retry, before the backoff sleep began: skip it. */
  private skipNextBackoff: boolean = false;
  /** The opening offset probe in `start()` is in flight. */
  private openingProbe: boolean = false;
  /** The in-flight part, for `wake()`; null between parts. */
  private partWatch: {
    lastProgressAt: number;
    loaded: number;
    size: number;
    abortForWake: () => void;
  } | null = null;
  /** performance.now() when the page last went hidden / offline mid-upload. */
  private lastHiddenAt: number | null = null;
  private lastOfflineAt: number | null = null;
  /** Set by a visible/online wake; reported on the next progress tick. */
  private returnMark: { at: number; reason: WakeReason; outcome: WakeOutcome | null } | null = null;

  /**
   * QA knob, non-production builds only: override the stall watchdog via
   * `localStorage.setItem('sv_stall_timeout_ms', '5000')`. A four-rung shrink
   * ladder costs 15 s per rung to reproduce at the production value; stage
   * testing shouldn't have to sit through it. Clamped to [1s, 30s] — the
   * ceiling stays under the 60s session watchdog (WATCHDOG_STALL_MS in
   * useHandleSubmitUploadedFile) so the invariant "chunk stall fires before
   * session watchdog" holds even under an override; at 60s the two would
   * race and resurrect the double-recovery overlap. Absent, malformed, or
   * production build → null (default applies). Logs loudly when active so a
   * forgotten key can't silently skew a later "production-representative"
   * test run in the same browser profile.
   */
  private static stallTimeoutOverrideMs(): number | null {
    try {
      if ((process.env.APP_ENV || '') === 'production') return null;
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('sv_stall_timeout_ms') : null;
      if (!raw) return null;
      const n = Number(raw);
      if (!Number.isFinite(n)) return null;
      const clamped = Math.min(30_000, Math.max(1_000, Math.round(n)));
      console.warn(
        `[Upload] QA OVERRIDE ACTIVE: stallTimeoutMs=${clamped} from localStorage 'sv_stall_timeout_ms'. ` +
          `Remove with localStorage.removeItem('sv_stall_timeout_ms') for production-representative timing.`,
      );
      return clamped;
    } catch {
      return null;
    }
  }

  constructor(options: UploadOptions) {
    this.speed = 1;
    this.id = options.id;
    this.uploadUrl = options.uploadUrl;
    this.file = options.file;
    this.chunkSize = options.initialChunkSize || 256 * 1024; // Default: 256KB
    this.maxRetries = options.maxRetries || 3;
    this.retryDelay = options.retryDelay || 2000;
    this.stallTimeoutMs = options.stallTimeoutMs || Upload.stallTimeoutOverrideMs() || 15000;
    this.onProgress = options.onProgress;
    this.onChunkMeasured = options.onChunkMeasured;
    this.trackEvent = options.trackEvent;
  }

  /* ─── Telemetry (spec v2) — counters only, no behaviour ─── */
  private tel = {
    started: false,
    startedAt: 0,
    startOffset: 0,
    pausedAt: null as number | null,
    pauseReason: null as string | null,
    firstByteAt: null as number | null,
    parts: 0,
    retries: 0,
    stalls: 0,
    minPart: Infinity,
    maxPart: 0,
    milestones: new Set<number>(),
    hiddenAt: null as number | null,
    hiddenMsTotal: 0,
    watchdogFired: false,
    visibilityHandler: null as (() => void) | null,
    offlineHandler: null as (() => void) | null,
  };

  private telPct(): number {
    return this.file.size > 0 ? Math.round((100 * this.maxReportedBytes) / this.file.size) : 0;
  }

  private telAttachVisibility(): void {
    if (typeof document === 'undefined' || this.tel.visibilityHandler) return;
    const handler = () => {
      if (document.visibilityState === 'hidden') {
        // Unload also flips visibility; that is upload_abandoned's job.
        if (isLeaving()) return;
        this.tel.hiddenAt = performance.now();
        this.lastHiddenAt = this.tel.hiddenAt;
        this.trackEvent({ action: EventNames.upload_backgrounded, pct: this.telPct() });
      } else if (this.tel.hiddenAt !== null) {
        const hiddenMs = Math.round(performance.now() - this.tel.hiddenAt);
        this.tel.hiddenMsTotal += hiddenMs;
        const before = this.tel.hiddenAt;
        this.tel.hiddenAt = null;
        this.trackEvent({
          action: EventNames.upload_foregrounded,
          pct: this.telPct(),
          hidden_ms: hiddenMs,
          progress_moved_while_hidden: (getAttempt()?.lastProgressAt ?? 0) > before,
        });
      }
    };
    document.addEventListener('visibilitychange', handler);
    this.tel.visibilityHandler = handler;
    // Not telemetry: `wake('online')` needs to know when the link went away.
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      const offline = () => {
        this.lastOfflineAt = performance.now();
      };
      window.addEventListener('offline', offline);
      this.tel.offlineHandler = offline;
    }
  }

  private telDetachVisibility(): void {
    if (this.tel.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.tel.visibilityHandler);
      this.tel.visibilityHandler = null;
    }
    if (this.tel.offlineHandler && typeof window !== 'undefined') {
      window.removeEventListener('offline', this.tel.offlineHandler);
      this.tel.offlineHandler = null;
    }
  }

  /** Reason code for `upload_failed`, from the error the engine gave up with. */
  private static failureReason(error: unknown): string {
    const e = error as { name?: string; message?: string } | null;
    if (!e) return 'unknown';
    if (e instanceof SessionExpiredError) return 'session_expired';
    if (e.name === 'ChunkStalledError' || /stalled/i.test(e.message || '')) return 'stall_budget';
    const m = /(\d{3})/.exec(e.message || '');
    if (m) return `http_${m[1]}`;
    if (/network/i.test(e.message || '')) return 'network';
    if (e.name === 'AbortError') return 'abort';
    return 'unknown';
  }

  /** Reads the upload data from localStorage */
  private static getStoredUploadData(): Record<string, any> {
    return JSON.parse(localStorage.getItem('uploadData') || '{}');
  }

  /** Saves this upload instance's data to localStorage */
  private saveUploadData(): void {
    const uploadData = Upload.getStoredUploadData();
    uploadData[this.id] = { uploadedBytes: this.uploadedBytes, chunkSize: this.chunkSize };
    localStorage.setItem('uploadData', JSON.stringify(uploadData));

    // Mirror into the resume-aware pendingUpload record so a cross-session
    // resume offer has up-to-date progress. No-op if the caller hasn't yet
    // written the parent record (e.g. during the URL-mint step).
    patchPendingUpload({
      uploadedBytes: this.uploadedBytes,
      chunkSize: this.chunkSize,
      mbpsEwma: this.mbpsEwma,
    });
  }

  /** Removes this upload instance's data from localStorage */
  private finalizeUpload(): void {
    const totalMs = Math.round(performance.now() - this.tel.startedAt);
    const firstByteMs = this.tel.firstByteAt !== null ? Math.round(this.tel.firstByteAt - this.tel.startedAt) : null;
    const transferMs = firstByteMs !== null ? totalMs - firstByteMs : null;
    // Average over bytes this attempt actually sent (a resume starts mid-file).
    const bytesSent = this.file.size - this.tel.startOffset;
    this.trackEvent({
      action: EventNames.upload_completed_v2,
      total_ms: totalMs,
      first_byte_ms: firstByteMs,
      transfer_ms: transferMs,
      parts: this.tel.parts,
      retries: this.tel.retries,
      stalls: this.tel.stalls,
      mbps_avg:
        transferMs && transferMs > 0 ? Math.round(((bytesSent * 8) / (transferMs / 1000) / 1e6) * 100) / 100 : null,
      mbps_ewma_final: this.mbpsEwma,
      bytes: this.file.size,
      bytes_sent: bytesSent,
      start_offset: this.tel.startOffset,
      min_part_bytes: Number.isFinite(this.tel.minPart) ? this.tel.minPart : null,
      max_part_bytes: this.tel.maxPart || null,
      watchdog_fired: this.tel.watchdogFired,
      hidden_ms_total: this.tel.hiddenMsTotal,
    });
    setAttemptMeta({ completed: true });
    const uploadData = Upload.getStoredUploadData();
    const uploadedEntry = uploadData[this.id];

    // Remove from uploadData
    delete uploadData[this.id];
    localStorage.setItem('uploadData', JSON.stringify(uploadData));

    // Move to uploadedData
    const uploadedData = JSON.parse(localStorage.getItem('uploadedData') || '{}');
    uploadedData[this.id] = {
      fileName: this.file.name,
      size: this.file.size,
      completedAt: new Date().toISOString(),
      ...uploadedEntry,
    };
    localStorage.setItem('uploadedData', JSON.stringify(uploadedData));

    // Resume record: bytes are up, story POST is pending. The caller clears
    // the record once CreateStory.fulfilled resolves.
    patchPendingUpload({
      uploadedBytes: this.file.size,
      status: 'finalizing',
    });
  }

  /** Starts or resumes an upload */
  async start(): Promise<void> {
    if (this.isUploading) {
      throw Object.assign(new Error('Upload already in progress'), { name: 'UploadAlreadyInProgressError' });
    }
    // Calling start() on a previously-paused instance means we're resuming —
    // clear the pause flag and the stale aborter so the chunk loop and fetch
    // can proceed. Without this, the loop's `!this.isPaused` guard would exit
    // on iteration zero and `uploadChunks` would re-throw UploadPausedError.
    // The Wi-Fi-later flow resumes by calling start() again on the paused
    // instance (resume() is never called by the app), so report it here.
    const resumingFromPause = this.isPaused;
    this.isPaused = false;
    this.chunkAbortController = null;
    this.isUploading = true;
    this.retrying = false;
    this.skipNextBackoff = false;
    if (resumingFromPause) {
      this.trackEvent({
        action: EventNames.upload_engine_resume,
        reason: this.tel.pauseReason ?? 'user',
        pct: this.telPct(),
        paused_ms: this.tel.pausedAt !== null ? Math.round(performance.now() - this.tel.pausedAt) : null,
      });
      this.tel.pausedAt = null;
      this.tel.pauseReason = null;
    }
    const firstStart = !this.tel.started;
    if (firstStart) {
      this.tel.started = true;
      this.tel.startedAt = performance.now();
      const attempt = getAttempt();
      this.trackEvent({
        action: EventNames.upload_attempt_start,
        file_size: this.file.size,
        path: attempt?.path ?? null,
        resumes_attempt_id: attempt?.resumesAttemptId ?? null,
        initial_part_bytes: this.chunkSize,
        speed_seed_mbps: attempt?.speedSeedMbps ?? null,
        speed_seed_source: attempt?.speedSeedMbps != null ? 'cached' : 'none',
        preflight_total_ms: msSinceAttempt(),
      });
    }
    this.telAttachVisibility();
    try {
      const uploadData = Upload.getStoredUploadData();
      this.uploadedBytes = uploadData[this.id]?.uploadedBytes || 0;
      // Opening offset probe, with the same fallback the chunk-loop stall
      // path has always had: on probe failure, proceed from the locally
      // recorded confirmed offset instead of throwing. Before this, a dead
      // probe failed the whole start() — and since the supervisor's 5s retry
      // loop begins every attempt with this probe, a user whose RTT exceeded
      // the probe timeout sat at ZERO upload attempts indefinitely (QA run 2,
      // episode B: 35s of no attempts under 5000ms latency, ended only when
      // the latency lifted). The local offset only advances on
      // server-acknowledged parts, so resuming from it is exactly the part-
      // boundary fallback the retry path already trusts. If the session URI
      // is truly dead (410), the first chunk PUT surfaces SessionExpiredError
      // through the normal path.
      const probeStartedAt = performance.now();
      this.openingProbe = true;
      try {
        this.uploadedBytes = await this.getUploadedBytes();
        if (firstStart) {
          this.trackEvent({
            action: EventNames.preflight_step,
            step: 'probe_308',
            outcome: 'ok',
            ms: Math.round(performance.now() - probeStartedAt),
          });
        }
      } catch (probeError) {
        if (firstStart) {
          const timedOut = (probeError as { name?: string } | null)?.name === 'AbortError';
          this.trackEvent({
            action: EventNames.preflight_step,
            step: 'probe_308',
            outcome: timedOut ? 'timeout' : 'fail',
            ms: Math.round(performance.now() - probeStartedAt),
          });
        }
        console.warn(
          `[Upload] opening offset probe failed; resuming from locally recorded offset ${this.uploadedBytes}.`,
          probeError,
        );
      } finally {
        this.openingProbe = false;
      }
      if (firstStart) {
        const attempt = getAttempt();
        this.trackEvent({
          action: EventNames.preflight_complete,
          total_ms: msSinceAttempt(),
          captcha_mode: attempt?.captchaMode ?? null,
          user_state: attempt?.userState ?? null,
          resumed_from_offset: this.uploadedBytes,
        });
      }
      // Seed the progress high-water mark from the server-side state so
      // mid-chunk progress events can never report below where we actually
      // are. Resume cases (server has bytes from a prior session) start with
      // a non-zero baseline.
      this.maxReportedBytes = this.uploadedBytes;
      this.saveUploadData();
      if (firstStart) {
        // Resumed attempts: milestones already passed at the start offset
        // are not this attempt's progress.
        this.tel.startOffset = this.uploadedBytes;
        const startPct = this.file.size > 0 ? (100 * this.uploadedBytes) / this.file.size : 0;
        setAttemptMeta({ startOffsetPct: Math.round(startPct) });
        for (const m of [25, 50, 75, 90]) if (startPct >= m) this.tel.milestones.add(m);
      }

      await this.uploadChunks();
    } catch (error) {
      const e = error as { name?: string } | null;
      const benign =
        error instanceof UploadPausedError ||
        e?.name === 'UploadAlreadyFinishedError' ||
        e?.name === 'UploadAlreadyInProgressError';
      if (!benign) {
        this.trackEvent({
          action: EventNames.upload_failed,
          reason_code: Upload.failureReason(error),
          error_class: e?.name ?? 'Error',
          bytes_done: this.uploadedBytes,
          pct: this.telPct(),
          parts_done: this.tel.parts,
          retries: this.tel.retries,
          stalls: this.tel.stalls,
          elapsed_ms: Math.round(performance.now() - this.tel.startedAt),
          last_progress_age_ms:
            getAttempt()?.lastProgressAt != null
              ? Math.round(performance.now() - (getAttempt()!.lastProgressAt as number))
              : null,
          will_mint_new: error instanceof SessionExpiredError,
        });
      }
      throw error;
    } finally {
      this.isUploading = false;
      this.telDetachVisibility();
    }
  }

  /**
   * Halts the upload. Sets the pause flag (so the chunk loop exits on its
   * next iteration) AND aborts any in-flight chunk PUT (so we don't wait for
   * the current chunk to finish before stopping). The aborted bytes are
   * harmless: GCS resumable PUTs only commit bytes that reached the server
   * before the connection dropped, and the 308 offset probe on resume tells
   * us exactly what stuck.
   */
  pause(reason: 'wifi_later' | 'offline' | 'hidden' | 'user' = 'wifi_later'): void {
    this.isPaused = true;
    if (this.chunkAbortController) {
      this.chunkAbortController.abort();
    }
    this.tel.pausedAt = performance.now();
    this.tel.pauseReason = reason;
    this.trackEvent({ action: EventNames.upload_engine_pause, reason, pct: this.telPct() });
    console.log(`Upload ${this.id} paused`);
  }

  /** Resumes the upload */
  async resume(): Promise<void> {
    if (this.isPaused) {
      this.isPaused = false;
      this.chunkAbortController = null;
      this.trackEvent({
        action: EventNames.upload_engine_resume,
        reason: this.tel.pauseReason ?? 'user',
        pct: this.telPct(),
        paused_ms: this.tel.pausedAt !== null ? Math.round(performance.now() - this.tel.pausedAt) : null,
      });
      this.tel.pausedAt = null;
      this.tel.pauseReason = null;
      console.log(`Resuming upload ${this.id}...`);
      await this.uploadChunks();
    }
  }

  /**
   * Nudges a running upload when the page returns to the foreground, the
   * network comes back, or the app's retry loop / the user asks for a
   * restart. The app's restart path refuses to run while `isUploadInProgress`
   * is held, which it is for the whole of the engine's own retry loop — so
   * without this, a return could sit out a backoff sleep or a dead part's
   * 15 s stall timer before anything moved. Never starts a second transfer:
   * it only shortens waits the running loop is already in.
   */
  async wake(reason: WakeReason): Promise<WakeOutcome> {
    const fromQuiet = reason === 'visible' || reason === 'online';
    const mark = fromQuiet ? { at: performance.now(), reason, outcome: null as WakeOutcome | null } : null;
    if (mark) this.returnMark = mark;
    const outcome = await this.wakeOnce(reason);
    if (mark) mark.outcome = outcome;
    // A paused upload waits for the user, not the return; nothing to time.
    if (mark && this.isPaused && this.returnMark === mark) this.returnMark = null;
    return outcome;
  }

  private async wakeOnce(reason: WakeReason): Promise<WakeOutcome> {
    if (!this.isUploading || this.isPaused) return 'noop_idle';
    if (this.backoffWake || this.retrying) {
      // While offline a retry would fail at once; the backoff is doing its job.
      const online = typeof navigator === 'undefined' || navigator.onLine !== false;
      return online ? this.cutRetryWait() : 'noop_offline';
    }
    if (this.openingProbe) return 'noop_probe';
    const part = this.partWatch;
    if (!part) return 'noop_idle';
    if (part.loaded >= part.size) return 'noop_awaiting_ack';
    const quietAt = reason === 'visible' ? this.lastHiddenAt : reason === 'online' ? this.lastOfflineAt : null;
    if (quietAt === null || part.lastProgressAt > quietAt) return 'noop_progressing';
    await new Promise((res) => setTimeout(res, Upload.WAKE_GRACE_MS));
    if (this.partWatch !== part) {
      // The part ended on its own during the grace window. If it failed and
      // the retry path took over, skip that retry's backoff instead.
      return this.backoffWake || this.retrying ? this.cutRetryWait() : 'noop_progressing';
    }
    if (part.lastProgressAt > quietAt) return 'noop_progressing';
    part.abortForWake();
    return 'aborted_stalled_part';
  }

  /** Runs the pending retry now: ends its backoff, or skips it before it starts. */
  private cutRetryWait(): WakeOutcome {
    if (this.backoffWake) {
      this.backoffWake();
      return 'woke_backoff';
    }
    this.skipNextBackoff = true;
    return 'woke_retry';
  }

  /**
   * Retry for a part `wake()` aborted: ask the server where it got to and
   * carry on from there at the same attempt number and part size.
   */
  private async retryAfterWake(offset: number, chunkEnd: number, attempt: number): Promise<number> {
    let retryOffset = offset;
    try {
      const serverOffset = await this.getUploadedBytes();
      if (serverOffset > retryOffset) retryOffset = Math.min(serverOffset, this.file.size);
    } catch (probeError) {
      console.warn(`Resume probe after wake failed; retrying from part boundary.`, probeError);
    }
    const committed = retryOffset >= chunkEnd;
    const retryEnd = committed ? retryOffset : Math.min(retryOffset + this.chunkSize, this.file.size);
    this.trackEvent({
      action: EventNames.upload_chunk_retry,
      chunkSize: retryEnd - retryOffset,
      attempt,
      stalled: false,
      resumedFromOffset: retryOffset > offset ? retryOffset : null,
      skippedBytes: retryOffset - offset,
      chunkFullyCommitted: committed,
    });
    this.tel.retries += 1;
    this.trackEvent({
      action: EventNames.upload_part_retry,
      attempt,
      reason: 'wake',
      backoff_ms: 0,
      resumed_from_offset: retryOffset > offset ? retryOffset : null,
      skipped_bytes: retryOffset - offset,
      shrunk_to_bytes: null,
      chunk_fully_committed: committed,
    });
    if (committed) return retryOffset;
    if (this.isPaused) throw new UploadPausedError();
    return this.uploadChunk(retryOffset, this.file.slice(retryOffset, retryEnd), retryEnd, attempt);
  }

  /** First progress tick after a visible/online wake: how long the return took to move bytes. */
  private reportReturnProgress(): void {
    const mark = this.returnMark;
    if (!mark) return;
    this.returnMark = null;
    this.trackEvent({
      action: EventNames.upload_progress_after_return,
      trigger: mark.reason,
      // Still null only when bytes moved during wake()'s grace window.
      wake_outcome: mark.outcome ?? 'noop_progressing',
      ms_to_next_progress: Math.round(performance.now() - mark.at),
    });
  }

  /** Retry backoff that `wake()` can cut short. */
  private sleepBackoff(ms: number): Promise<void> {
    if (this.skipNextBackoff) {
      this.skipNextBackoff = false;
      return Promise.resolve();
    }
    return new Promise((res) => {
      const timer = setTimeout(() => {
        this.backoffWake = null;
        res();
      }, ms);
      this.backoffWake = () => {
        clearTimeout(timer);
        this.backoffWake = null;
        res();
      };
    });
  }

  /** Gets the last uploaded byte offset */
  private async getUploadedBytes(): Promise<number> {
    // Bound the resume probe. On a dead socket this PUT would otherwise hang
    // indefinitely — wedging `start()` before the first chunk, where the
    // chunk-level stall watchdog can't reach it. Capped at PROBE_TIMEOUT_MS
    // (see its doc comment): a header-only status question deserves seconds of
    // patience, not the 15s sized for data transfers. On timeout the abort
    // rejects `fetch`; in `start()` that propagates to the caller's error path
    // (→ setUploadError → existing recovery loop), and in the stall-retry path
    // it's caught and the retry proceeds from the chunk boundary.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), Math.min(this.stallTimeoutMs, Upload.PROBE_TIMEOUT_MS));
    let response: Response;
    try {
      response = await fetch(this.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Range': `bytes */${this.file.size}` },
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timer);
    }

    if (response.status === 308) {
      const rangeHeader = response.headers.get('Range');
      if (rangeHeader) {
        const match = rangeHeader.match(/bytes=(\d+)-(\d+)/);
        if (match) return parseInt(match[2], 10) + 1;
      }
    }

    return 0;
  }

  /** Uploads chunks until the file is fully uploaded */
  private async uploadChunks(): Promise<void> {
    let offset = this.uploadedBytes;
    let chunkIndex = Math.floor(this.uploadedBytes / this.chunkSize);

    while (offset < this.file.size && !this.isPaused) {
      const chunkEnd = Math.min(offset + this.chunkSize, this.file.size);
      const chunk = this.file.slice(offset, chunkEnd);

      // `uploadChunk` returns the offset actually reached rather than the
      // `chunkEnd` we asked for: a stall retry may shrink the part and/or
      // resume from a server-confirmed offset inside it, so the part that
      // ultimately lands can start and end somewhere else entirely. Driving
      // the loop off the returned value keeps `uploadedBytes` honest.
      let nextOffset: number;
      try {
        nextOffset = await this.uploadChunk(offset, chunk, chunkEnd);
      } catch (error) {
        console.error(`Upload ${this.id} failed:`, error);
        throw error;
      }

      const deliveredBytes = nextOffset - offset;
      offset = nextOffset;
      this.uploadedBytes = offset;
      this.saveUploadData();

      if (this.onProgress) {
        this.onProgress({
          totalBytes: this.file.size,
          uploadedBytes: this.uploadedBytes,
          chunkIndex,
          chunkLength: deliveredBytes,
        });
      }

      if (this.onChunkMeasured) {
        const remaining = this.file.size - this.uploadedBytes;
        const etaSeconds = this.mbpsEwma && this.mbpsEwma > 0 ? (remaining * 8) / (this.mbpsEwma * 1_000_000) : null;
        this.onChunkMeasured({
          chunkBytes: deliveredBytes,
          mbpsEwma: this.mbpsEwma ?? 0,
          uploadedBytes: this.uploadedBytes,
          totalBytes: this.file.size,
          etaSeconds,
        });
      }

      chunkIndex++;
    }

    if (offset >= this.file.size) {
      console.log(`Upload ${this.id} complete`);
      this.finalizeUpload();
      return;
    }

    // Loop exited with bytes remaining — only valid reason is an explicit
    // pause. Surface as a typed error so the caller short-circuits before
    // dispatching completion state or posting the story against an
    // unfinalized GCS object.
    if (this.isPaused) {
      throw new UploadPausedError();
    }
  }

  /**
   * PUTs a chunk via XHR (not fetch) so we can subscribe to
   * `xhr.upload.onprogress` and emit mid-chunk progress events. Browser
   * `fetch` only exposes upload progress via `ReadableStream` bodies +
   * `duplex: 'half'`, which Safari does not support — and Safari is the
   * platform that benefits most from smooth progress during long chunks.
   *
   * Wires `chunkAbortController.signal` to `xhr.abort()` so `pause()`
   * cancels in-flight chunks the same way it did with `fetch({ signal })`.
   */
  private putChunkWithProgress(
    offset: number,
    chunk: Blob,
    chunkEnd: number,
  ): Promise<{ status: number; statusText: string; ackMs: number | null }> {
    return new Promise((resolve, reject) => {
      const signal = this.chunkAbortController!.signal;
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }

      const xhr = new XMLHttpRequest();
      xhr.open('PUT', this.uploadUrl);
      xhr.setRequestHeader('Content-Range', `bytes ${offset}-${chunkEnd - 1}/${this.file.size}`);

      // Stall watchdog. A dropped mobile connection (cellular dead zone, tower
      // handoff, backgrounded iOS tab) often leaves the PUT in limbo: the socket
      // delivers neither `load` nor `error`, so without this the chunk — and the
      // whole upload — hangs forever with no recovery (the #1 cause of silently
      // stuck uploads). We trip a timer if no upload-progress event arrives for
      // `stallTimeoutMs`, abort, and reject with a *retryable* error. A
      // live-but-slow link keeps emitting progress ticks (even ~0.2 Mbps moves
      // bytes within the window), so this only fires on a genuinely idle pipe.
      let stalled = false;
      let wokenAbort = false;
      // Bytes this XHR reported handing to the socket, per the last progress
      // event. Reported on the stall event so we can tell "died before a byte
      // moved" from "died 90% of the way through a part".
      let lastLoaded = 0;
      // Timestamp of the most recent upload-progress event. The gap between
      // it and the `load` event is the serialized server-ack wait — the ~1 RTT
      // per part during which zero bytes flow. Measured here (rather than
      // assumed) so part sizing can amortize the *actual* cost; see
      // `uploadChunk` and getChunkSizeFromUploadSpeed's ratio mode.
      let lastProgressAt: number | null = null;
      let stallTimer: ReturnType<typeof setTimeout> | null = null;
      const clearStall = () => {
        if (stallTimer !== null) {
          clearTimeout(stallTimer);
          stallTimer = null;
        }
      };
      const armStall = () => {
        clearStall();
        stallTimer = setTimeout(() => {
          stalled = true;
          this.trackEvent({
            action: EventNames.upload_chunk_stalled,
            location: window.location.href,
            chunkSize: chunk.size,
            stallTimeoutMs: this.stallTimeoutMs,
            chunkOffset: offset,
            loadedBytes: lastLoaded,
          });
          this.tel.stalls += 1;
          this.trackEvent({
            action: EventNames.upload_part_stalled,
            stall_timeout_ms: this.stallTimeoutMs,
            offset,
            loaded_bytes: lastLoaded,
            part_bytes: chunk.size,
            pct: this.telPct(),
          });
          xhr.abort();
        }, this.stallTimeoutMs);
      };

      const onAbort = () => xhr.abort();
      signal.addEventListener('abort', onAbort);
      const watch = {
        lastProgressAt: performance.now(),
        loaded: 0,
        size: chunk.size,
        abortForWake: () => {
          wokenAbort = true;
          xhr.abort();
        },
      };
      this.partWatch = watch;
      const cleanup = () => {
        clearStall();
        signal.removeEventListener('abort', onAbort);
        if (this.partWatch === watch) this.partWatch = null;
      };

      // Mid-chunk progress: emit a fresh `onProgress` per native upload tick
      // (~50–100 ms in modern browsers). `event.loaded` is bytes sent for
      // *this* chunk; add `offset` to express it as a position in the file.
      // `Math.max(maxReportedBytes, ...)` prevents the ring from rewinding
      // if a chunk fails mid-flight and retries from byte 0 of the chunk.
      // Display ceiling for in-flight bytes. `event.loaded` counts bytes handed
      // to the SOCKET, not bytes the server received — on mobile the entire
      // final part can sit in kernel buffers with megabytes still in flight,
      // which used to print 100% (and downstream, a "complete" UI) for uploads
      // that never finished. Cap the final part's display so the rounded
      // percent stays ≤99 until the server actually acknowledges the last PUT;
      // the loop's post-success onProgress reports the true `file.size`.
      const displayCeiling =
        chunkEnd >= this.file.size ? Math.max(offset, Math.floor(this.file.size * 0.994)) : this.file.size;
      xhr.upload.addEventListener('progress', (event) => {
        // Bytes are moving — push the stall deadline out.
        armStall();
        lastLoaded = event.loaded;
        lastProgressAt = performance.now();
        watch.lastProgressAt = lastProgressAt;
        watch.loaded = event.loaded;
        noteUploadProgress();
        this.reportReturnProgress();
        if (event.loaded > 0 && this.tel.firstByteAt === null) {
          this.tel.firstByteAt = performance.now();
          this.trackEvent({
            action: EventNames.upload_first_byte,
            ms_since_attempt: msSinceAttempt(),
            ms_since_engine_start: Math.round(this.tel.firstByteAt - this.tel.startedAt),
            part_bytes: chunk.size,
          });
        }
        const pctNow = this.file.size > 0 ? Math.floor((100 * (offset + event.loaded)) / this.file.size) : 0;
        for (const m of [25, 50, 75, 90]) {
          if (pctNow >= m && !this.tel.milestones.has(m)) {
            this.tel.milestones.add(m);
            this.trackEvent({
              action: EventNames.upload_progress_milestone,
              pct: m,
              elapsed_ms: Math.round(performance.now() - this.tel.startedAt),
              mbps_ewma: this.mbpsEwma,
              retries_so_far: this.tel.retries,
              stalls_so_far: this.tel.stalls,
            });
          }
        }
        if (!event.lengthComputable || !this.onProgress) return;
        const inFlight = offset + event.loaded;
        const reported = Math.max(this.maxReportedBytes, Math.min(inFlight, displayCeiling));
        this.maxReportedBytes = reported;
        this.onProgress({
          totalBytes: this.file.size,
          uploadedBytes: reported,
          chunkIndex: Math.floor(offset / this.chunkSize),
          chunkLength: chunk.size,
        });
      });

      xhr.addEventListener('load', () => {
        cleanup();
        resolve({
          status: xhr.status,
          statusText: xhr.statusText,
          // null when no progress event fired (part swallowed whole before the
          // first tick) — the caller falls back to the conservative estimate.
          ackMs: lastProgressAt !== null ? performance.now() - lastProgressAt : null,
        });
      });
      xhr.addEventListener('error', () => {
        cleanup();
        reject(new Error('Network error during chunk upload'));
      });
      xhr.addEventListener('abort', () => {
        cleanup();
        // Distinguish our stall-triggered abort (retryable) from a user pause
        // (benign). The caller maps `AbortError` → UploadPausedError and halts,
        // so the stall path must NOT surface as AbortError or the upload would
        // stop silently instead of retrying.
        if (stalled) {
          reject(new ChunkStalledError());
        } else if (wokenAbort) {
          reject(new WakeAbortError());
        } else {
          reject(new DOMException('Aborted', 'AbortError'));
        }
      });

      xhr.send(chunk);
      // Arm for the window before the first progress tick — covers a socket
      // that dies before a single byte leaves.
      armStall();
    });
  }

  /**
   * Uploads a single chunk with retries. Resolves with the file offset
   * actually reached — normally `chunkEnd`, but a stall retry can shrink the
   * part and/or resume from a server-confirmed offset, so the caller must not
   * assume the requested `chunkEnd` was the one that landed.
   */
  private async uploadChunk(offset: number, chunk: Blob, chunkEnd: number, attempt = 1): Promise<number> {
    const startTime = performance.now();
    const partIndex = this.tel.parts;
    this.trackEvent({
      action: EventNames.upload_part_start,
      part_index: partIndex,
      offset,
      part_bytes: chunk.size,
      attempt_no: attempt,
    });
    // Fresh controller per chunk so `pause()` mid-flight aborts only the
    // current PUT, not future ones.
    this.chunkAbortController = new AbortController();
    try {
      const response = await this.putChunkWithProgress(offset, chunk, chunkEnd);

      if (response.status === 410) {
        // Session URI expired (GCS 7-day TTL). Surface as a distinct error so
        // the caller can mint a fresh URL instead of burning chunk retries.
        throw new SessionExpiredError();
      }

      const ok = response.status >= 200 && response.status < 300;
      if (!ok && response.status !== 308) {
        throw new Error(`Failed chunk upload: ${response.status} - ${response.statusText}`);
      }

      // Measure upload speed and smooth via EWMA to avoid chunk-size flapping.
      //
      // A part's wall time splits into TRANSFER (bytes moving) and the
      // serialized server-ACK wait (~1 RTT, zero bytes moving — QA's
      // competitive benchmark measured ~1s/part, ~11s per median upload, most
      // of our speed gap). Dividing by the full roundtrip therefore
      // systematically underestimates the link, which fed adjustChunkSize
      // undersized parts, which raised the overhead fraction further. Subtract
      // the measured ack instead — honest by construction, no separate
      // estimator to bias. Guards: when the ack wasn't measurable or the
      // implied transfer window is a sliver of the roundtrip (tiny part
      // swallowed by socket buffers), fall back to the conservative roundtrip
      // number — the next part grows regardless; and clamp the sample so one
      // freak reading can't launch the EWMA (and with it part sizing) into
      // orbit.
      const elapsedTime = (performance.now() - startTime) / 1000; // seconds
      const ackSec = response.ackMs !== null ? response.ackMs / 1000 : null;
      const transferSec = ackSec !== null ? elapsedTime - ackSec : null;
      const trustTransfer = transferSec !== null && transferSec >= Math.max(0.25, elapsedTime * 0.2);
      const rawSpeedMbps = trustTransfer
        ? (chunk.size * 8) / ((transferSec as number) * 1_000_000)
        : (chunk.size * 8) / (elapsedTime * 1_000_000);
      const speedMbps = Math.min(rawSpeedMbps, Upload.MAX_SPEED_SAMPLE_MBPS);
      this.mbpsEwma =
        this.mbpsEwma === null ? speedMbps : Upload.EWMA_ALPHA * speedMbps + (1 - Upload.EWMA_ALPHA) * this.mbpsEwma;
      this.speed = this.mbpsEwma;
      // Smooth the ack the same way. Clamped to [0.05s, 3s]: the floor keeps a
      // same-machine mock from zeroing the ratio; the cap keeps one 15s outlier
      // (e.g. a GC pause on the server) from demanding 4-minute parts.
      if (ackSec !== null) {
        const clampedAck = Math.min(3, Math.max(0.05, ackSec));
        this.ackEwmaSec =
          this.ackEwmaSec === null
            ? clampedAck
            : Upload.EWMA_ALPHA * clampedAck + (1 - Upload.EWMA_ALPHA) * this.ackEwmaSec;
      }
      this.tel.parts += 1;
      this.tel.minPart = Math.min(this.tel.minPart, chunk.size);
      this.tel.maxPart = Math.max(this.tel.maxPart, chunk.size);
      this.trackEvent({
        action: EventNames.upload_part_done,
        part_index: partIndex,
        part_bytes: chunk.size,
        transfer_ms: transferSec !== null ? Math.round(transferSec * 1000) : null,
        ack_ms: response.ackMs !== null ? Math.round(response.ackMs) : null,
        part_duration_ms: Math.round(elapsedTime * 1000),
        mbps: Math.round(speedMbps * 100) / 100,
        mbps_ewma: Math.round(this.mbpsEwma * 100) / 100,
        speed_source: trustTransfer ? 'paced' : 'roundtrip',
        bytes_done: chunkEnd,
        total_bytes: this.file.size,
      });
      // First confirmed part lets Clarity and Crisp load (see uploadQuiet).
      markPartConfirmed();
      this.adjustChunkSize(this.mbpsEwma);
      return chunkEnd;
    } catch (error) {
      // Expired session URI is unrecoverable at this layer — propagate so the
      // caller can re-init with a fresh URL.
      if (error instanceof SessionExpiredError) {
        throw error;
      }
      // User-initiated pause aborted this chunk. Convert to UploadPausedError
      // so the outer loop / caller can treat it as a benign halt rather than
      // a network failure. Burning retries here would defeat the abort.
      if (this.isPaused || (error instanceof DOMException && error.name === 'AbortError')) {
        throw new UploadPausedError();
      }
      // `wake()` aborted a part the OS had silently killed while the page was
      // hidden or offline. Resume from the server-confirmed byte right away:
      // no backoff, no shrink, and the same attempt number, because the part
      // never got a fair chance on the current link.
      if (error instanceof WakeAbortError) {
        return this.retryAfterWake(offset, chunkEnd, attempt);
      }
      console.error(`Chunk upload error on attempt ${attempt} for upload ${this.id}:`, error);

      if (attempt < this.maxRetries) {
        console.warn(`Retrying chunk upload... Attempt ${attempt + 1}`);
        this.retrying = true;

        const stalled = error instanceof ChunkStalledError;
        let retryOffset = offset;
        let shrunkTo: number | null = null;
        let resumedFrom: number | null = null;

        // ── (b) Resume mid-chunk — on EVERY retryable failure, not only
        // stalls. GCS commits what it received in 256 KiB increments, so a
        // part that died 80% through has most of its bytes durably stored.
        // Ask the server where it actually got to and restart from there
        // instead of from the part boundary. This mattered at 5 MiB parts;
        // with ratio-sized parts (up to 32 MiB) a plain network error at 90%
        // would otherwise re-send ~29 MiB of bytes the server already has —
        // the probe is what makes larger parts safe to run.
        //
        // The probe is bounded (see `getUploadedBytes`), so a dead socket
        // can't turn recovery into a second hang. If it fails we simply keep
        // the old behaviour and resume from the part boundary.
        try {
          const serverOffset = await this.getUploadedBytes();
          if (serverOffset > retryOffset) {
            retryOffset = Math.min(serverOffset, this.file.size);
            resumedFrom = retryOffset;
          }
        } catch (probeError) {
          console.warn(`Resume probe failed; retrying from part boundary.`, probeError);
        }

        // The failure raced a completing PUT — the server already has this
        // whole part. Nothing left to re-send; report where it landed.
        if (retryOffset >= chunkEnd) {
          this.trackEvent({
            action: EventNames.upload_chunk_retry,
            chunkSize: 0,
            attempt: attempt + 1,
            stalled,
            resumedFromOffset: retryOffset,
            skippedBytes: retryOffset - offset,
            chunkFullyCommitted: true,
          });
          this.tel.retries += 1;
          this.trackEvent({
            action: EventNames.upload_part_retry,
            attempt: attempt + 1,
            reason: stalled ? 'stall' : Upload.failureReason(error),
            backoff_ms: 0,
            resumed_from_offset: retryOffset,
            skipped_bytes: retryOffset - offset,
            shrunk_to_bytes: null,
            chunk_fully_committed: true,
          });
          this.retrying = false;
          this.skipNextBackoff = false;
          return retryOffset;
        }

        if (stalled) {
          // Feed the downgrade into the throughput model *before* shrinking,
          // using what the link actually demonstrated during the failed
          // attempt. Without this, the next successful part calls
          // `adjustChunkSize` with the old healthy EWMA and jumps straight
          // back to the size that just stalled.
          this.degradeEwmaAfterStall((performance.now() - startTime) / 1000, retryOffset - offset);

          // ── (a) Shrink after stall ────────────────────────────────────────
          shrunkTo = this.shrinkChunkSizeAfterStall();
        }

        const retryEnd = Math.min(retryOffset + this.chunkSize, this.file.size);
        const retryChunk = this.file.slice(retryOffset, retryEnd);

        this.trackEvent({
          action: EventNames.upload_chunk_retry,
          chunkSize: retryChunk.size,
          attempt: attempt + 1,
          stalled,
          // Only meaningful on the stall path; null elsewhere keeps the
          // dimension present and unambiguous rather than absent.
          shrunkToBytes: shrunkTo,
          resumedFromOffset: resumedFrom,
          skippedBytes: retryOffset - offset,
        });
        this.tel.retries += 1;
        this.trackEvent({
          action: EventNames.upload_part_retry,
          attempt: attempt + 1,
          reason: stalled ? 'stall' : Upload.failureReason(error),
          backoff_ms: this.retryDelay * attempt,
          resumed_from_offset: resumedFrom,
          skipped_bytes: retryOffset - offset,
          shrunk_to_bytes: shrunkTo,
          chunk_fully_committed: false,
        });

        // Wait before retrying (exponential backoff). `wake()` cuts it short
        // when the page returns or the network comes back.
        await this.sleepBackoff(this.retryDelay * attempt);
        this.retrying = false;
        this.skipNextBackoff = false;

        // The resume probe and the backoff are both awaited, so the user can
        // hit "I'll use Wi-Fi later" in the middle of them. `pause()` aborts
        // the controller for the PUT that already failed, which no longer
        // stops anything — so re-check the flag here rather than starting a
        // fresh PUT the user has already asked us to stop.
        if (this.isPaused) {
          throw new UploadPausedError();
        }

        return this.uploadChunk(retryOffset, retryChunk, retryEnd, attempt + 1);
      }

      console.error(`Max retries reached. Upload ${this.id} failed.`);
      // Carry the shrink across the retry-budget seam. When the per-chunk
      // budget exhausts on a stall, the OUTER layers (hook-level re-start,
      // session error loop) restart the chunk loop — previously at the
      // UNCHANGED part size, so the first post-restart attempt repeated the
      // size that had just stalled (QA window-C ladder: 5 → 2.5 → 1.25 →
      // 1.25 again → 1). Shrinking here means the restart begins one rung
      // down, saving a full stall timeout on genuinely bad links.
      if (error instanceof ChunkStalledError) {
        this.shrinkChunkSizeAfterStall();
      }
      throw new Error(`Upload failed after ${this.maxRetries} retries.`);
    }
  }

  /**
   * Halves the working part size after a stall, floored at
   * `STALL_MIN_CHUNK_BYTES` (1 MiB — deliberately below the normal 5 MiB
   * floor; see its doc comment) and aligned to GCS's 256 KiB granularity. Never grows the part: the
   * recorded-video path can start below the floor (the 256 KiB constructor
   * default), and a "shrink" that quietly inflated the part would walk
   * straight back into the stall it was meant to escape.
   */
  private shrinkChunkSizeAfterStall(): number {
    const aligned = Math.floor(this.chunkSize / 2 / CHUNK_ALIGNMENT) * CHUNK_ALIGNMENT;
    const shrunk = Math.min(this.chunkSize, Math.max(Upload.STALL_MIN_CHUNK_BYTES, aligned));
    if (shrunk !== this.chunkSize) {
      console.log(`Chunk size shrunk after stall: ${this.chunkSize / 1024 / 1024} MB → ${shrunk / 1024 / 1024} MB`);
      this.chunkSize = shrunk;
    }
    return this.chunkSize;
  }

  /**
   * Folds a stall into the EWMA as a bandwidth downgrade.
   *
   * A stall isn't a random hiccup — it says the link can no longer sustain
   * what it was doing when we sized this part. The sample we credit it with is
   * what the link actually demonstrated: bytes the server confirmed, over the
   * time the failed attempt ran. That sample replaces the EWMA outright rather
   * than blending at α=0.3, because a downgrade that arrives at 30% weight
   * takes several parts to show up — several more stalls.
   *
   * Two guards: the result never rises (a downgrade must not look like good
   * news if the pre-stall EWMA was already lower), and it never falls below
   * `MIN_EWMA_MBPS`. A stall where nothing landed would otherwise produce a
   * 0 Mbps sample, pinning part sizing to the floor and the ETA at infinity
   * for the rest of the upload even after the line recovers.
   */
  private degradeEwmaAfterStall(elapsedSeconds: number, deliveredBytes: number): void {
    const previous = this.mbpsEwma;
    const observed =
      elapsedSeconds > 0 && deliveredBytes > 0 ? (deliveredBytes * 8) / (elapsedSeconds * 1_000_000) : null;

    // Nothing measurable got through: fall back to halving what we believed.
    const next =
      observed === null ? (previous === null ? null : previous / 2) : Math.min(observed, previous ?? observed);
    if (next === null) return;

    this.mbpsEwma = Math.max(next, Upload.MIN_EWMA_MBPS);
    this.speed = this.mbpsEwma;
  }

  /** Dynamically adjusts chunk size based on upload speed */
  private adjustChunkSize(speedMbps: number): void {
    const previousSize = this.chunkSize;
    this.chunkSize = getChunkSizeFromUploadSpeed(speedMbps, this.ackEwmaSec ?? undefined);

    if (previousSize !== this.chunkSize) {
      console.log(`Chunk size updated to: ${this.chunkSize / 1024 / 1024} MB`);
    }
  }
}
